import { ModalsProvider as MantineModalsProvider, modals, ContextModalProps } from '@mantine/modals';
import { Text, Button, Group, Stack } from '@mantine/core';
import { ReactNode } from 'react';

export interface ModalsProviderProps {
  children: ReactNode;
}

// Confirm modal content component
const ConfirmModal = ({
  context,
  id,
  innerProps,
}: ContextModalProps<{
  title: string;
  body: string;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: string;
}>) => {
  const { title, body, onConfirm, confirmLabel = 'Confirm', cancelLabel = 'Cancel', confirmColor = 'blue' } = innerProps;

  return (
    <Stack gap="md">
      <Text size="sm">{body}</Text>
      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={() => context.closeModal(id)}>
          {cancelLabel}
        </Button>
        <Button
          color={confirmColor}
          onClick={() => {
            onConfirm();
            context.closeModal(id);
          }}
        >
          {confirmLabel}
        </Button>
      </Group>
    </Stack>
  );
};

// Delete confirm modal
const DeleteModal = ({
  context,
  id,
  innerProps,
}: ContextModalProps<{
  itemName: string;
  onDelete: () => void;
}>) => {
  const { itemName, onDelete } = innerProps;

  return (
    <Stack gap="md">
      <Text size="sm">
        Are you sure you want to delete <strong>{itemName}</strong>? This action cannot be undone.
      </Text>
      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={() => context.closeModal(id)}>
          Cancel
        </Button>
        <Button
          color="red"
          onClick={() => {
            onDelete();
            context.closeModal(id);
          }}
        >
          Delete
        </Button>
      </Group>
    </Stack>
  );
};

export function ModalsProvider({ children }: ModalsProviderProps) {
  return (
    <MantineModalsProvider
      modals={{
        confirm: ConfirmModal,
        delete: DeleteModal,
      }}
      modalProps={{
        centered: true,
        overlayProps: {
          backgroundOpacity: 0.55,
          blur: 3,
        },
        styles: {
          content: {
            backgroundColor: 'var(--mantine-color-body)',
          },
          header: {
            backgroundColor: 'var(--mantine-color-body)',
          },
          body: {
            padding: '1.5rem',
          },
        },
      }}
    >
      {children}
    </MantineModalsProvider>
  );
}

// Modal helper functions
export const modal = {
  confirm: (props: {
    title: string;
    body: string;
    onConfirm: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmColor?: string;
  }) => {
    modals.openContextModal({
      modal: 'confirm',
      title: props.title,
      innerProps: props,
    });
  },

  delete: (props: { itemName: string; onDelete: () => void }) => {
    modals.openContextModal({
      modal: 'delete',
      title: 'Delete Confirmation',
      innerProps: props,
    });
  },

  open: modals.open,
  close: modals.close,
  closeAll: modals.closeAll,
};

export default ModalsProvider;
